import {
  applySizeToImageUrl,
  Device,
  IconLabel,
  IconName,
  ImageSizeVariant,
  Layout,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Scale,
  useDeviceViewport,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as moment from 'moment';
import * as React from 'react';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext, IApplication } from '../../common/contexts/ApplicationContext';
import {
  BookingDetailsDatesArrowHead,
  BookingDetailsDatesArrowLine,
  BookingDetailsDatesArrowWrapper,
} from '../../common/utils/dateArrow.styles';
import { getLocaleSettings } from '../../common/utils/getLocaleSettingsUtils';
import { LoadingSkeletonSection } from '../../common/utils/loadingSkeletons.styles';
import {
  StyledTooltipContent,
  StyledTooltipTitle,
} from '../BookingAmendments/BookingAmendments.styles';
import { AspectRatioWrapper } from '../SaleCard/SaleCardSkeleton/SaleCardSkeleton.styles';
import { PAYMENT_LOGO_SWEDEN, PAYMENT_LOGO_UK } from './BookingOfferDetails.assets';
import {
  IBookingDetailsCancellationInformationProps,
  IBookingDetailsHeaderProps,
  IBookingDetailsImageProps,
  IBookingDetailsLogoProps,
  IBookingDetailsPageProps,
  IBookingHotelDetailPopupProps,
} from './BookingOfferDetails.interfaces';
import {
  AmendedOfferDetailsContainer,
  BookingDatesItemWrapper,
  BookingDetailsDatesContainer,
  BookingDetailsDatesWrapper,
  CancellationInformationContainer,
  CustomLogoImage,
  CustomNormalImage,
  IconLabelContainer,
  OriginalOfferDetailsContainer,
  RefundInformationContainer,
  StyledResponsiveTypography,
  WarningContainer,
} from './BookingOfferDetails.styles';

const DATE_FORMAT: string = 'ddd DD MMM YYYY';

const BookingDetailsHeader: React.FunctionComponent<IBookingDetailsHeaderProps> = ({
  booking,
  isMobile,
  theme,
}) => {
  return (
    <div data-testid="BookingDetailsHeader">
      <StyledResponsiveTypography
        color={isMobile ? theme.color.white : theme.color.blackText}
        variant={ResponsiveTypographyVariant.t5SansBold}
      >
        {booking.title}
      </StyledResponsiveTypography>
      <ResponsiveTypography
        color={isMobile ? theme.color.white : theme.color.blackText}
        variant={ResponsiveTypographyVariant.body}
      >
        {booking.destinationName}
      </ResponsiveTypography>
    </div>
  );
};

const BookingDetailsRefundInformation: React.FunctionComponent<IBookingDetailsCancellationInformationProps> = ({
  cancellationInformation,
  isCancellable,
}) => {
  const { translations } = React.useContext(TranslationsContext);

  return (
    <React.Fragment>
      {(isCancellable || cancellationInformation) && (
        <RefundInformationContainer>
          {isCancellable && (
            <IconLabelContainer data-testid="RefundableLabel">
              <IconLabel backgroundColor={'#008B71'} icon={IconName.shield}>
                {i18n('cancellation.refundable.label', [], translations)}
              </IconLabel>
            </IconLabelContainer>
          )}
          {cancellationInformation && isCancellable && (
            <CancellationInformationContainer data-testid="CancellationInfo">
              <ResponsiveTypography
                variant={ResponsiveTypographyVariant.body}
                allowDangerousRender={true}
              >
                {cancellationInformation}
              </ResponsiveTypography>
            </CancellationInformationContainer>
          )}
          {cancellationInformation && !isCancellable && (
            <CancellationInformationContainer data-testid="NonCancellableInfo">
              <WarningContainer>
                <ResponsiveTypography
                  variant={ResponsiveTypographyVariant.body}
                  allowDangerousRender={true}
                >
                  {cancellationInformation}
                </ResponsiveTypography>
              </WarningContainer>
            </CancellationInformationContainer>
          )}
        </RefundInformationContainer>
      )}
    </React.Fragment>
  );
};

const BookingDetailsLogo: React.FunctionComponent<IBookingDetailsLogoProps> = ({
  userTerritory,
  isWhitelabel,
}) => {
  return (
    <Layout.Container alignItems={'flex-start'} justifyContent={'flex-end'}>
      {userTerritory === 'UK' && !isWhitelabel && (
        <div data-testid="BookingDetailsLogoUK">
          <CustomLogoImage
            alt={'Trustpilot logo'}
            size={ImageSizeVariant.fluid}
            srcSet={[{ src: PAYMENT_LOGO_UK }]}
          />
        </div>
      )}
      {userTerritory === 'SE' && (
        <div data-testid="BookingDetailsLogoSE">
          <CustomLogoImage
            alt={'Nortin secure logo'}
            size={ImageSizeVariant.fluid}
            srcSet={[{ src: PAYMENT_LOGO_SWEDEN }]}
          />
        </div>
      )}
    </Layout.Container>
  );
};

const BookingDetailsDatesArrow: React.FunctionComponent = () => {
  return (
    <BookingDetailsDatesArrowWrapper>
      <BookingDetailsDatesArrowLine />
      <BookingDetailsDatesArrowHead />
    </BookingDetailsDatesArrowWrapper>
  );
};

const BookingHotelDetailsPopupContent: React.FunctionComponent<IBookingHotelDetailPopupProps> = ({
  offerDetails,
  originalOfferDetails,
}) => {
  const { translations } = React.useContext(TranslationsContext);

  return (
    <StyledTooltipContent>
      <AmendedOfferDetailsContainer>
        <StyledTooltipTitle variant={ResponsiveTypographyVariant.t4SansBold}>
          {i18n('sale.amended.booking.offerSummary', [], translations)}
        </StyledTooltipTitle>
        <ResponsiveTypography
          variant={ResponsiveTypographyVariant.body}
          allowDangerousRender={true}
        >
          {offerDetails}
        </ResponsiveTypography>
      </AmendedOfferDetailsContainer>
      <OriginalOfferDetailsContainer>
        <StyledTooltipTitle variant={ResponsiveTypographyVariant.t4SansBold}>
          {i18n('sale.original.booking.offerSummary', [], translations)}
        </StyledTooltipTitle>
        <ResponsiveTypography
          variant={ResponsiveTypographyVariant.body}
          allowDangerousRender={true}
        >
          {originalOfferDetails}
        </ResponsiveTypography>
      </OriginalOfferDetailsContainer>
    </StyledTooltipContent>
  );
};

const BookingDetailsDates: React.FunctionComponent<IBookingDetailsPageProps> = ({ booking }) => {
  const { translations } = React.useContext(TranslationsContext);
  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const locale: string = getLocaleSettings(applicationProps.territoryConfig.posLocale);
  const startDate: string = moment(booking.bookingDates.start)
    .locale(locale)
    .format(DATE_FORMAT);
  const endDate: string = moment(booking.bookingDates.end)
    .locale(locale)
    .format(DATE_FORMAT);

  return (
    <BookingDetailsDatesContainer>
      <BookingDetailsDatesWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.bodyBold}>
            {startDate}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
            {i18n('common.nights.text', [booking.duration], translations)}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <ResponsiveTypography variant={ResponsiveTypographyVariant.bodyBold}>
          {endDate}
        </ResponsiveTypography>
      </BookingDetailsDatesWrapper>
      <BookingDetailsDatesWrapper>
        <BookingDatesItemWrapper>
          <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
            {i18n('cancellation.details.checkin.date', [], translations)}
          </ResponsiveTypography>
        </BookingDatesItemWrapper>
        <BookingDetailsDatesArrow />
        <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
          {i18n('cancellation.details.checkout.date', [], translations)}
        </ResponsiveTypography>
      </BookingDetailsDatesWrapper>
    </BookingDetailsDatesContainer>
  );
};

const BookingDetailsImage: React.FunctionComponent<IBookingDetailsImageProps> = ({ leadImage }) => {
  const device: Device = useDeviceViewport();
  let imageUrl: string = '';
  const size: { width: number; height: number } = BookingOfferDetailsImageSize[device];
  if (leadImage) {
    imageUrl = leadImage.urlWithSize
      ? applySizeToImageUrl(leadImage.urlWithSize, size.width, size.height)
      : leadImage.url;
  }

  return (
    <div data-testid="BookingImage">
      {leadImage && (
        <CustomNormalImage
          fallback={
            <AspectRatioWrapper>
              <LoadingSkeletonSection
                marginBottom={Scale.m}
                width="100%"
                height="100%"
                background={true}
                borderRadius={`${Scale.xxs} ${Scale.xxs} 0 0`}
              />
            </AspectRatioWrapper>
          }
          alt={leadImage.caption}
          size={ImageSizeVariant.fluid}
          srcSet={[{ src: imageUrl }]}
        />
      )}
    </div>
  );
};

const BookingOfferDetailsImageSize: {
  [d in Device]: { height: number; width: number };
} = {
  [Device.mobile]: { width: 375, height: 250 },
  [Device.tablet]: { width: 330, height: 230 },
  [Device.desktop]: { width: 330, height: 230 },
};

export {
  BookingOfferDetailsImageSize,
  BookingDetailsRefundInformation,
  BookingHotelDetailsPopupContent,
  BookingDetailsDates,
  BookingDetailsHeader,
  BookingDetailsImage,
  BookingDetailsLogo,
};
